import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/i18n";
import { photos } from "@/data/site";
import { PageHero } from "@/components/site/PageHero";
import { Section } from "@/components/site/Section";
import { Reveal } from "@/components/site/Reveal";
import { Comfort } from "@/components/site/Comfort";
import { CtaBanner } from "@/components/site/CtaBanner";
import { SEO } from "@/components/site/SEO";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function Casa() {
  const { t } = useLang();
  return (
    <>
      <SEO title={t.pages.casa.seo} description={t.pages.casa.desc} path="/la-casa" />
      <PageHero image={photos.soggiorno} eyebrow={t.pages.casa.eyebrow} title={t.pages.casa.title} testid="casa-hero" />

      <section className="py-16 sm:py-24" data-testid="casa-story">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <Reveal>
            <p className="text-base leading-relaxed text-ink-muted sm:text-lg">{t.casaPage.p1}</p>
            <p className="mt-4 text-base leading-relaxed text-ink-muted sm:text-lg">{t.casaPage.p2}</p>
            <div className="mt-10 grid grid-cols-4 gap-3">
              {t.welcome.stats.map((s) => (
                <div key={s.l} className="rounded-2xl border border-border bg-white p-4 text-center shadow-sm">
                  <div className="font-serif text-2xl font-semibold text-terracotta sm:text-3xl">{s.v}</div>
                  <div className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-ink-muted">{s.l}</div>
                </div>
              ))}
            </div>
          </Reveal>
          <div className="grid grid-cols-2 gap-4">
            <Reveal><img src={photos.cucinaTavola} alt="Cucina con tavolo apparecchiato" loading="lazy" className="h-72 w-full rounded-2xl object-cover shadow-lg" /></Reveal>
            <Reveal delay={0.1}><img src={photos.bagno} alt="Bagno moderno con doccia walk-in" loading="lazy" className="mt-8 h-72 w-full rounded-2xl object-cover shadow-lg" /></Reveal>
            <Reveal delay={0.15}><img src={photos.mandala} alt="Mandala disegnato a mano sulla parete" loading="lazy" className="h-56 w-full rounded-2xl object-cover shadow-lg" /></Reveal>
            <Reveal delay={0.2}><img src={photos.limoni} alt="Limoni di Sicilia sul tavolo" loading="lazy" className="mt-8 h-56 w-full rounded-2xl object-cover shadow-lg" /></Reveal>
          </div>
        </div>
      </section>

      <Comfort />

      <section className="bg-sand-deep py-16 sm:py-24" data-testid="casa-pets-gift">
        <div className="mx-auto grid max-w-7xl gap-6 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <Reveal>
            <div className="group relative h-96 overflow-hidden rounded-3xl shadow-xl">
              <img src={photos.amiciPelosi} alt="Due cani riposano sul divano di casa" loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/15 to-transparent" />
              <div className="absolute bottom-0 p-8">
                <h3 className="font-serif text-2xl font-semibold text-sand sm:text-3xl">{t.casaPage.petsTitle}</h3>
                <p className="mt-2 max-w-md text-sm leading-relaxed text-sand/85">{t.casaPage.petsText}</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="group relative h-96 overflow-hidden rounded-3xl shadow-xl">
              <img src={photos.regalo} alt="Regalo di benvenuto artigianale" loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/15 to-transparent" />
              <div className="absolute bottom-0 p-8">
                <h3 className="font-serif text-2xl font-semibold text-sand sm:text-3xl">{t.casaPage.giftTitle}</h3>
                <p className="mt-2 max-w-md text-sm leading-relaxed text-sand/85">{t.casaPage.giftText}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24" data-testid="casa-faq">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <Section title={t.casaPage.faqTitle} testid="faq-heading" center />
          <Accordion type="single" collapsible className="w-full">
            {t.casaPage.faq.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-serif text-lg font-semibold text-ink">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-ink-muted sm:text-base">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <Reveal className="mt-10 text-center">
            <Link to="/prenota" data-testid="casa-booking-cta" className="inline-flex items-center gap-2 rounded-full bg-terracotta px-7 py-3.5 text-sm font-bold text-sand shadow-lg shadow-terracotta/25 transition-all duration-200 hover:bg-terracotta-dark hover:-translate-y-0.5">
              {t.hero.cta2} <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      <CtaBanner />
    </>
  );
}
